// Icon source identifiers for multi-library support
export type IconSource = 'si' | 'mdi' | 'carbon' | 'lucide' | 'tabler' | 'devicon' | 'custom';

// Icon categories for filtering
export type IconCategory = 
  | 'all'
  | 'brands'
  | 'infrastructure'
  | 'network'
  | 'cloud'
  | 'database'
  | 'frontend'
  | 'backend'
  | 'devops'
  | 'security'
  | 'monitoring'
  | 'storage';

export interface IconData {
  title: string;
  /** 
   * Namespaced icon identifier (e.g., "si:docker", "mdi:server")
   * For backward compatibility, can also be just the slug (defaults to SimpleIcons)
   */
  icon: string;
  /** @deprecated Use `icon` instead. Kept for backward compatibility */
  slug?: string;
  /** Default hex color without # */
  hex: string;
  /** Category for filtering in sidebar */
  category?: IconCategory;
}

/** Size modifier for nodes */
export type NodeSizeMod = 'small' | 'default' | 'large';

export interface TechNodeData {
  label: string;
  /** 
   * Namespaced icon identifier (e.g., "si:docker", "mdi:server")
   * Takes precedence over iconSlug if both are present
   */
  icon?: string;
  /** @deprecated Use `icon` instead. Kept for backward compatibility with saved diagrams */
  iconSlug?: string;
  description?: string;
  hex?: string;
  isAI?: boolean;
  /** Whether the node is locked (prevents movement) */
  locked?: boolean;
  /** Whether to show the node border (default: true) */
  showBorder?: boolean;
  /** Node background color (hex without #) */
  nodeColor?: string;
  /** Node border color (hex without #) */
  borderColor?: string;
  /** Size modifier for the node */
  sizeMod?: NodeSizeMod;
  /** Semantic layout hints from AI generation — consumed by services/layoutService.ts */
  layout?: GeneratedNode['layout'];
  onSnapshot?: () => void;
}

export interface GroupNodeData {
  label: string;
  /** Whether the node is locked (prevents movement) */
  locked?: boolean;
  onSnapshot?: () => void;
}

export interface StickyNoteData {
  label: string;
  color?: 'yellow' | 'blue' | 'green' | 'pink' | 'purple' | 'red';
  /** Whether the node is locked (prevents movement) */
  locked?: boolean;
  /** Solid fill instead of the default translucent one (default: false) */
  opaque?: boolean;
  onSnapshot?: () => void;
}

export interface EdgeData {
  /**
   * Where the label sits along the edge path, 0 (source) → 1 (target).
   * Undefined means the midpoint. The label can't leave the path.
   */
  labelT?: number;
  /** Label text/border colour as a hex string. Undefined means the neutral default. */
  labelColor?: string;
}

export interface TextNodeData {
  label: string;
  description?: string;
  hex?: string;
  /** Whether the node is locked (prevents movement) */
  locked?: boolean;
  /** Whether to show the node border (default: true) */
  showBorder?: boolean;
  /** Node background color (hex without #) */
  nodeColor?: string;
  /** Node border color (hex without #) */
  borderColor?: string;
  /** Size modifier for the node */
  sizeMod?: NodeSizeMod;
  onSnapshot?: () => void;
}

export type ShapeType = 'square' | 'circle' | 'triangle' | 'diamond' | 'hexagon' | 'star' | 'parallelogram' | 'cylinder';

export interface ShapeNodeData {
  label: string;
  /** Shape type to render */
  shape: ShapeType;
  /** Fill color (hex without #) */
  fillColor?: string;
  /** Border color (hex without #) */
  borderColor?: string;
  /** Whether the node is locked (prevents movement) */
  locked?: boolean;
  onSnapshot?: () => void;
}

export enum AppState {
  IDLE = 'IDLE',
  GENERATING = 'GENERATING',
  ERROR = 'ERROR',
}

export interface GeneratedStack {
  nodes: GeneratedNode[];
  edges: GeneratedEdge[];
  groups?: GeneratedGroup[];
  stickyNotes?: GeneratedStickyNote[];
  textNodes?: GeneratedTextNode[];
  /** Short model-authored summary surfaced as the assistant's chat reply. */
  notes?: string;
}

export interface GeneratedNode {
  id: string;
  label: string;
  /** 
   * Namespaced icon identifier (e.g., "si:docker", "mdi:server")
   * Falls back to simpleicons slug if no namespace
   */
  icon: string;
  description: string;
  /** Optional category for styling and organization */
  category?: 'frontend' | 'backend' | 'database' | 'infrastructure' | 'devops' | 'security' | 'monitoring' | 'storage' | 'network' | 'cloud';
  /** Importance level for visual hierarchy (1=highest, 5=lowest) */
  importance?: 1 | 2 | 3 | 4 | 5;
  /** Optional group ID if this node belongs to a group */
  groupId?: string;
  /**
   * Semantic layout hints authored by the model. The deterministic placer in
   * services/layoutService.ts turns these into coordinates; when absent or
   * unusable we fall back to dagre auto-layout.
   */
  layout?: {
    /** 0-based depth along the main flow (0 = entry point/client). Each hop downstream increments by 1. */
    layer?: number;
    /** 0-based slot within the layer; the primary-path node uses 0. */
    orderInLayer?: number;
    /** True only for nodes on the single primary path (the visual spine). */
    mainPath?: boolean;
  };
  /** Optional metadata for advanced features */
  metadata?: {
    version?: string;
    status?: 'stable' | 'beta' | 'deprecated';
    notes?: string;
  };
}

export interface GeneratedEdge {
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  /** Label describing the relationship (e.g., "REST API", "gRPC", "publishes to") */
  label?: string;
  /** Type of relationship for styling */
  type?: 'data' | 'api' | 'event' | 'dependency' | 'control';
  /** Whether this edge is bidirectional */
  bidirectional?: boolean;
}

export interface GeneratedGroup {
  id: string;
  label: string;
  /** Optional description of what this group contains */
  description?: string;
  /** List of node IDs in this group */
  nodeIds: string[];
  /** Color for the group (hex without #) */
  color?: string;
}

export interface GeneratedStickyNote {
  id: string;
  label: string;
  /** Position relative to other elements */
  position?: 'top' | 'right' | 'bottom' | 'left' | 'center';
  /** Color of the sticky note */
  color?: 'yellow' | 'blue' | 'green' | 'pink' | 'purple' | 'red';
  /** Optional node ID this note is attached to */
  attachedToNodeId?: string;
}

export interface GeneratedTextNode {
  id: string;
  label: string;
  /** Optional description/subtitle */
  description?: string;
  /** Position relative to other elements */
  position?: 'top' | 'right' | 'bottom' | 'left' | 'center';
}

/**
 * Partial-update protocol for conversational revision: instead of echoing the
 * full stack (where absence meant deletion), the model returns only the delta
 * and absence is a no-op. Applied by services/aiPatch.ts onto the current
 * stack; the patched stack then flows through the regular position-preserving
 * merge (mergeRevisedStack).
 */
export interface GeneratedPatch {
  /** Model's one-line explanation of the change — surfaced as the chat reply. */
  notes?: string;
  /** New nodes (fresh `new_`-prefixed ids). */
  addNodes?: GeneratedNode[];
  /** Field updates for existing nodes, matched by id; only set changed fields. */
  updateNodes?: Array<Partial<GeneratedNode> & { id: string }>;
  /** Ids to remove — matched across nodes, sticky notes and text nodes. */
  removeNodeIds?: string[];
  /** Connections to add; endpoints must exist after this patch. */
  addEdges?: GeneratedEdge[];
  /** Connections to remove, matched by source+target (and label when given). */
  removeEdges?: Array<{ source: string; target: string; label?: string }>;
  /** New groups. */
  addGroups?: GeneratedGroup[];
  /** Updates to existing groups: rename and/or reassign membership wholesale. */
  updateGroups?: Array<Partial<GeneratedGroup> & { id: string }>;
  /** Groups to dissolve — their members stay on the canvas, ungrouped. */
  removeGroupIds?: string[];
  addStickyNotes?: GeneratedStickyNote[];
  addTextNodes?: GeneratedTextNode[];
}

// Save status for cloud sync
export enum SaveStatus {
  IDLE = 'idle',
  SAVING = 'saving',
  SAVED = 'saved',
  ERROR = 'error',
  UNSAVED = 'unsaved',
}

// Current diagram state for editor
export interface CurrentDiagram {
  id: string | null;
  name: string;
  isModified: boolean;
}

// Folder for organizing diagrams
export interface Folder {
  id: string;
  name: string;
  color: string | null;
  icon: string | null;
  userId: string | null;
  createdAt: string;
  updatedAt: string;
}

// Diagram metadata for dashboard listing
export interface DiagramMetadata {
  id: string;
  name: string;
  description: string | null;
  folderId: string | null;
  nodeCount: number;
  edgeCount: number;
  isPublic: boolean;
  shareToken: string | null;
  isStarred: boolean;
  icon: string | null;
  iconColor: string | null;
  createdAt: string;
  updatedAt: string;
}

// Public diagram data (for shared view)
export interface PublicDiagram {
  id: string;
  name: string;
  description: string | null;
  nodes: import('reactflow').Node[];
  edges: import('reactflow').Edge[];
  viewport: import('reactflow').Viewport | null;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

// Share toggle response
export interface ShareToggleResponse {
  id: string;
  isPublic: boolean;
  shareToken: string | null;
}

// Star toggle response
export interface StarToggleResponse {
  id: string;
  isStarred: boolean;
}

// AI Provider types. 'custom' is any OpenAI-compatible endpoint (OpenRouter,
// Together, LM Studio, a local proxy…) configured with a base URL + model.
export type AIProvider = 'gemini' | 'openai' | 'anthropic' | 'custom';

export interface AIProviderConfig {
  id: AIProvider;
  name: string;
  description: string;
  docsUrl: string;
  keyPlaceholder: string;
  keyPrefix?: string;
  isAvailable: boolean; // Whether the provider is currently implemented
}

// AI Generation result
export interface AIGenerationResult {
  success: boolean;
  data?: GeneratedStack;
  error?: AIGenerationError;
  /** True when the user cancelled mid-run — callers reset quietly. */
  aborted?: boolean;
  /** Model-authored summary preferred over the deterministic counts reply. */
  notes?: string;
  /** Post-repair layout quality, when the repair loop ran or was assessable. */
  quality?: {
    score: number;
    repairRounds: number;
    issues: import('./services/geometryValidation').GeometryIssue[];
  };
}

/**
 * Live progress during a generation run, emitted by generateTechStackWithAI
 * and the repair loop. Drives the modal's activity feed.
 */
export type AIGenerationProgressEvent =
  | { type: 'start' }
  | { type: 'generated' }
  | { type: 'validating' }
  | { type: 'validated'; score: number; issueCount: number }
  | { type: 'repairing'; round: number; score: number; issueCount: number }
  | { type: 'repaired'; round: number; score: number }
  | { type: 'done'; score: number; repairRounds: number };

export interface AIGenerationError {
  code: 'NO_API_KEY' | 'INVALID_API_KEY' | 'RATE_LIMIT' | 'NETWORK_ERROR' | 'TIMEOUT' | 'GENERATION_FAILED';
  message: string;
  provider: AIProvider;
}

